import { Component, computed, DestroyRef, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import {
  CellClickedEvent,
  ColDef,
  FilterModel,
  FirstDataRenderedEvent,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridState,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowSelectionOptions,
  StateUpdatedEvent,
} from 'ag-grid-community';
import { select, Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AgGridAngular } from 'ag-grid-angular';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DialogService } from 'primeng/dynamicdialog';
import { ApplicationEvent, CertifiableSystem } from '@workspace-src/app/odatabackend/QualicoatBackendModel';
import { TimelineDialogComponent } from '@workspace-app/shared/timeline/components/timeline-dialog/timeline-dialog.component';
import { PermissionService, QCT_ROLES } from '@workspace-app/shared/services/permission.service';
import {
  ActionMenuItem,
  ActionsMenuComponent,
} from '@workspace-app/shared/components/actions-menu/actions-menu.component';
import { GlDataService } from '@workspace-app/crm/shared/data-services/gl-data.service';
import { ApprovedSystemsDataService } from './approved-systems-data.service';
import { getApprovedSystemsColumns } from './approved-systems.columns';
import { PageComponent, User } from '../../../../shared';
import { GlobalStateActions, selectGridStates, selectUser } from '../../../../state/global';
import { ApprovedSystemOverlayComponent } from '../approved-system-view/approved-system-overlay.component';
import { ApprovedSystemsGridService } from '../../services/approved-systems-grid.service';
import { ApprovedSystemsTimelineService } from '../../services/approved-systems-timeline.service';
import { BulkRenewalDialogComponent } from '../../components/bulk-renewal-dialog/bulk-renewal-dialog.component';
import { BulkDownloadDialogComponent } from '../../components/bulk-download-dialog/bulk-download-dialog.component';
import { ShareCertificatesDialogComponent } from '../../../shared/certificate-sharing/share-certificates-dialog/share-certificates-dialog.component';

@Component({
  selector: 'app-approved-systems',
  imports: [PageComponent, AgGridAngular, ButtonModule, TooltipModule, FontAwesomeModule, ActionsMenuComponent],
  providers: [DialogService],
  templateUrl: './approved-systems.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './approved-systems.component.scss',
})
export class ApprovedSystemsComponent {
  private permissionService = inject(PermissionService);
  private router = inject(Router);
  private glDataService = inject(GlDataService);

  colDefs: ColDef[] = [];

  // User signal for permission checks
  private user = signal<User | undefined>(undefined);

  // Quick filter state signals
  activeStateFilter = signal<string | null>(null);
  activeSystemTypeFilter = signal<string | null>(null);
  hasActiveFilters = computed(() => this.activeStateFilter() !== null || this.activeSystemTypeFilter() !== null);

  // Permission check for bulk renewal (License.Admin)
  canManageApprovals = computed(() => this.permissionService.hasEffectivePermission(this.user(), 'License.Admin'));

  // "Share Certificates" is open to QCT and GL users (a GL only sees its own suppliers — enforced by the API)
  canShareCertificates = computed(() => this.permissionService.hasAnyEffectiveRole(this.user(), [...QCT_ROLES, 'GL']));

  // Actions menu items
  actionMenuItems = computed<ActionMenuItem[]>(() => [
    {
      label: 'Create System',
      icon: 'far fa-plus',
      command: () => this.router.navigate(['/certification/approved-systems/new']),
      visible: this.canManageApprovals(),
    },
    {
      label: 'Bulk Renew Certificates',
      icon: 'far fa-certificate',
      command: () => this.openBulkRenewalDialog(),
      visible: this.canManageApprovals(),
    },
    {
      label: 'Bulk Download Certificates',
      icon: 'far fa-download',
      command: () => this.openBulkDownloadDialog(),
      visible: this.canManageApprovals() || this.canShareCertificates(),
    },
    {
      label: 'Share Certificates',
      icon: 'far fa-share-nodes',
      command: () => this.openShareCertificatesDialog(),
      visible: this.canShareCertificates(),
    },
  ]);

  // Check if any action menu items are visible
  hasVisibleActions = computed(() => this.actionMenuItems().some((item) => item.visible));

  // Grid state persistence
  private stateUpdateTimeout?: number;
  private preservedFilterModel: FilterModel | null = null;
  private isInitializing = true;
  private isFirstDataRendered = false;

  gridOptions: GridOptions = {
    defaultColDef: {
      resizable: true,
      enablePivot: false,
    },
    rowModelType: 'serverSide',
    pivotMode: false,
    columnMenu: 'new',
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivots: true,
            suppressPivotMode: true,
            suppressRowGroups: true,
            suppressValues: true,
          },
        },
      ],
    },
    rowHeight: 42,
    pagination: true,
    paginationPageSize: 50,
    cacheBlockSize: 50,
    autoSizeStrategy: {
      type: 'fitGridWidth',
      defaultMinWidth: 100,
    },
    getRowId: this.getRowId,
  };

  public rowSelection: RowSelectionOptions | 'single' | 'multiple' = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
  };

  initialGridState = {} as GridState;
  private gridApi?: GridApi;
  private gridKey = 'approvedSystemsGrid';

  destroyRef = inject(DestroyRef);
  store = inject(Store);
  dialogService = inject(DialogService);
  approvedSystemsDataService = inject(ApprovedSystemsDataService);
  approvedSystemsGridService = inject(ApprovedSystemsGridService);
  approvedSystemsTimelineService = inject(ApprovedSystemsTimelineService);

  constructor() {
    this.colDefs = getApprovedSystemsColumns(
      {
        openTimeline: (system: CertifiableSystem) => this.openTimeline(system),
      },
      {
        glNamesFetcher: (params) =>
          this.glDataService.fetchGlNamesForFilter().subscribe((names) => params.success(names)),
      }
    );

    // Subscribe to user for permission checks
    this.store.pipe(select(selectUser), takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      this.user.set(user);
    });

    // Subscribe to reload trigger from the grid service
    this.approvedSystemsGridService.reloadTrigger
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reloadGridData());

    // Subscribe to grid states for initial state
    this.store.pipe(select(selectGridStates), takeUntilDestroyed(this.destroyRef)).subscribe((gridStates) => {
      if (gridStates && gridStates[this.gridKey]) {
        this.initialGridState = { ...gridStates[this.gridKey] };
      } else {
        this.initialGridState = {} as GridState;
      }
    });

    // Cleanup timeout on component destruction
    this.destroyRef.onDestroy(() => {
      clearTimeout(this.stateUpdateTimeout);
    });
  }

  getRowId(params: GetRowIdParams<CertifiableSystem, CertifiableSystem>): string {
    // Class-3 coating systems in Granting state have no Number yet; fall back to the system id.
    return params.data.number == null ? params.data.id! : params.data.prefix + '-' + params.data.number.toString();
  }

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;

    const dataSource: IServerSideDatasource = {
      getRows: (params: IServerSideGetRowsParams) => {
        this.approvedSystemsDataService.getRows(params);
      },
    };

    // Preserve the ENTIRE filter model from the restored grid state BEFORE setting datasource
    this.preservedFilterModel = this.gridApi?.getFilterModel() || null;

    // Set the datasource immediately - filter restoration will happen in firstDataRendered
    this.gridApi.setGridOption('serverSideDatasource', dataSource);
  }

  onCellClicked($event: CellClickedEvent): void {
    const headerName = $event.column.getColDef().headerName;
    if (headerName !== 'Actions') {
      const system = $event.data as CertifiableSystem;
      this.view(system);
    }
  }

  view(system: CertifiableSystem): void {
    this.dialogService.open(ApprovedSystemOverlayComponent, {
      header: `System Approval ${system.completeApprovalNumber}`,
      position: 'right',
      height: '100%',
      closable: true,
      draggable: false,
      maskStyleClass: 'mask',
      modal: true,
      baseZIndex: 1000,
      contentStyle: { overflow: 'auto' },
      data: {
        system,
      },
    });
  }

  private reloadGridData(): void {
    if (!this.gridApi) return;
    this.gridApi.refreshServerSide({ purge: true });
  }

  onFirstDataRendered(_event: FirstDataRenderedEvent) {
    this.isFirstDataRendered = true;

    // Restore filter state after first data is rendered to ensure proper timing
    if (this.preservedFilterModel && Object.keys(this.preservedFilterModel).length > 0) {
      // Use a small timeout to ensure ag-grid is fully ready to accept filter changes
      setTimeout(() => {
        this.gridApi?.setFilterModel(this.preservedFilterModel);
        this.updateQuickFiltersFromModel(this.preservedFilterModel);
      }, 100);
    } else {
      // No preserved filters, ensure quick filters are cleared
      this.updateQuickFiltersFromModel({});
    }

    // Mark initialization as complete after filter restoration
    setTimeout(() => {
      this.isInitializing = false;
    }, 200);
  }

  private updateQuickFiltersFromModel(filterModel: FilterModel | null) {
    const filterObj = filterModel as Record<string, unknown> | null;

    // Update state filter signal
    const stateFilter = filterObj?.['approvalState'] as { values?: string[] } | undefined;
    if (stateFilter && stateFilter.values && stateFilter.values.length === 1) {
      this.activeStateFilter.set(stateFilter.values[0]);
    } else {
      this.activeStateFilter.set(null);
    }

    // Update system type filter signal
    const typeFilter = filterObj?.['systemType'] as { values?: string[] } | undefined;
    if (typeFilter && typeFilter.values && typeFilter.values.length > 0) {
      // Check which quick filter matches these values
      const pretreatmentValues = this.systemTypeFilterValues['Pretreatment'];
      const powderValues = this.systemTypeFilterValues['Powder'];

      if (pretreatmentValues.some((v) => typeFilter.values!.includes(v))) {
        this.activeSystemTypeFilter.set('Pretreatment');
      } else if (powderValues.some((v) => typeFilter.values!.includes(v))) {
        this.activeSystemTypeFilter.set('Powder');
      } else {
        this.activeSystemTypeFilter.set(null);
      }
    } else {
      this.activeSystemTypeFilter.set(null);
    }
  }

  stateUpdated($event: StateUpdatedEvent) {
    // Skip state updates during initialization phase to prevent race conditions
    if (this.isInitializing || !this.isFirstDataRendered) {
      return;
    }

    // Skip initialization-related events that can cause race conditions
    const initializationSources = ['gridInitializing', 'columnResizing', 'sorting', 'columnMoved', 'columnVisible'];
    if ($event.sources && $event.sources.some((source) => initializationSources.includes(source))) {
      return;
    }

    // Debounce rapid-fire state updates to avoid race conditions
    clearTimeout(this.stateUpdateTimeout);
    this.stateUpdateTimeout = window.setTimeout(() => {
      this.processStateUpdate($event);
    }, 100);
  }

  private processStateUpdate($event: StateUpdatedEvent) {
    // Get current filter model and update quick filter states
    const currentFilterModel = this.gridApi?.getFilterModel();
    this.updateQuickFiltersFromModel(currentFilterModel || null);

    // Store the current grid state
    const gridState = $event.api?.getState();
    this.store.dispatch(GlobalStateActions.storeGridState({ key: this.gridKey, gridState }));
  }

  resetTableState() {
    // Reset quick filter signals
    this.activeStateFilter.set(null);
    this.activeSystemTypeFilter.set(null);
    // Reset filters only — the user's column layout (visibility, order, widths) is kept
    this.gridApi?.setFilterModel(null);
  }

  adjustColumnSizes() {
    this.gridApi?.sizeColumnsToFit();
  }

  // Quick filter methods
  filterByState(state: string): void {
    if (this.activeStateFilter() === state) {
      this.clearStateFilter();
    } else {
      this.activeStateFilter.set(state);
      const filterModel = this.gridApi?.getFilterModel() || {};
      filterModel['approvalState'] = {
        filterType: 'set',
        values: [state],
      };
      this.gridApi?.setFilterModel(filterModel);
    }
  }

  // System type filter mappings
  private systemTypeFilterValues: Record<string, string[]> = {
    Pretreatment: [
      'Chemical Pretreatment System',
      'Chemical Pretreatment System (for new technology)',
      'Chemical Pretreatment System (for pre anodised material)',
    ],
    Powder: ['Coating System', 'Organic Coating System for Sublimation'],
  };

  filterBySystemType(type: string): void {
    if (this.activeSystemTypeFilter() === type) {
      this.clearSystemTypeFilter();
    } else {
      this.activeSystemTypeFilter.set(type);
      const filterModel = this.gridApi?.getFilterModel() || {};
      const values = this.systemTypeFilterValues[type] || [type];
      filterModel['systemType'] = {
        filterType: 'set',
        values: values,
      };
      this.gridApi?.setFilterModel(filterModel);
    }
  }

  clearStateFilter(): void {
    this.activeStateFilter.set(null);
    const filterModel = this.gridApi?.getFilterModel() || {};
    delete filterModel['approvalState'];
    this.gridApi?.setFilterModel(filterModel);
  }

  clearSystemTypeFilter(): void {
    this.activeSystemTypeFilter.set(null);
    const filterModel = this.gridApi?.getFilterModel() || {};
    delete filterModel['systemType'];
    this.gridApi?.setFilterModel(filterModel);
  }

  clearAllQuickFilters(): void {
    this.activeStateFilter.set(null);
    this.activeSystemTypeFilter.set(null);
    const filterModel = this.gridApi?.getFilterModel() || {};
    delete filterModel['approvalState'];
    delete filterModel['systemType'];
    this.gridApi?.setFilterModel(filterModel);
  }

  openBulkRenewalDialog(): void {
    const dialogRef = this.dialogService.open(BulkRenewalDialogComponent, {
      header: 'Bulk Certificate Renewal',
      width: '500px',
      modal: true,
      closable: true,
      closeOnEscape: false,
      draggable: false,
    });

    dialogRef?.onClose.subscribe((result: { completed: boolean; status: unknown } | undefined) => {
      if (result?.completed) {
        // Refresh the grid if renewal was completed
        this.reloadGridData();
      }
    });
  }

  openBulkDownloadDialog(): void {
    this.dialogService.open(BulkDownloadDialogComponent, {
      header: 'Bulk Download Certificates',
      width: '500px',
      modal: true,
      closable: true,
      draggable: false,
      // The "all suppliers by certification year" mode is admin-only; GL users only get the
      // per-organisation download (scoped to their suppliers by the API).
      data: { byYearAllowed: this.canManageApprovals() },
    });
  }

  openShareCertificatesDialog(): void {
    this.dialogService.open(ShareCertificatesDialogComponent, {
      header: 'Share Certificates',
      width: '640px',
      modal: true,
      closable: true,
      draggable: false,
      data: { kind: 'approved-systems' },
    });
  }

  openTimeline(system: CertifiableSystem): void {
    if (!system) return;

    // Custom icon mapper for approved system events
    const iconMapper = (eventType: string, _eventData?: unknown) => {
      if (eventType === 'certifiable_system.state_changed') {
        return 'arrows-turn-to-dots';
      }
      if (eventType === 'certifiable_system.created') {
        return 'plus-circle';
      }
      if (eventType === 'certifiable_system.updated') {
        return 'pen-to-square';
      }
      if (eventType === 'certifiable_system.archived') {
        return 'inbox';
      }
      if (eventType === 'certifiable_system.restored') {
        return 'undo';
      }
      if (eventType === 'certifiable_system.certificate_renewed') {
        return 'calendar-plus';
      }
      if (eventType === 'certifiable_system.certificate_generated') {
        return 'file-certificate';
      }
      if (eventType === 'certifiable_system.attributes_updated') {
        return 'sliders';
      }
      if (eventType === 'certifiable_system.imported') {
        return 'file-import';
      }
      if (eventType === 'document.uploaded') {
        return 'file-arrow-up';
      }

      // Default icons based on event type patterns
      if (eventType.includes('state_changed')) return 'arrows-turn-to-dots';
      if (eventType.includes('color')) return 'palette';
      if (eventType.includes('created')) return 'plus-circle';
      if (eventType.includes('updated')) return 'pen-to-square';
      if (eventType.includes('archived')) return 'inbox';
      if (eventType.includes('restored')) return 'undo';

      return 'info-circle';
    };

    // Custom color mapper for approved system events
    const iconColorMapper = (eventType: string, _eventData?: unknown) => {
      if (eventType === 'certifiable_system.state_changed') {
        return '#ffc107'; // Amber for state changes
      }
      if (eventType === 'certifiable_system.created') {
        return '#007bff'; // Blue for created
      }
      if (eventType === 'certifiable_system.updated') {
        return '#17a2b8'; // Cyan for updated
      }
      if (eventType === 'certifiable_system.archived') {
        return '#6c757d'; // Gray for archived
      }
      if (eventType === 'certifiable_system.restored') {
        return '#28a745'; // Green for restored
      }
      if (eventType === 'certifiable_system.certificate_renewed') {
        return '#17a2b8'; // Teal for certificate renewed
      }
      if (eventType === 'certifiable_system.certificate_generated') {
        return '#28a745'; // Green for certificate generated
      }
      if (eventType === 'certifiable_system.attributes_updated') {
        return '#17a2b8'; // Cyan for attributes updated
      }
      if (eventType === 'certifiable_system.imported') {
        return '#9c27b0'; // Purple for imported
      }
      if (eventType === 'document.uploaded') {
        return '#607d8b'; // Blue-gray for documents
      }

      // Default colors based on event type patterns
      if (eventType.includes('state_changed')) return '#ffc107';
      if (eventType.includes('created')) return '#007bff';
      if (eventType.includes('updated')) return '#17a2b8';
      if (eventType.includes('archived')) return '#6c757d';
      if (eventType.includes('restored')) return '#28a745';

      return '#6c757d';
    };

    // Custom title mapper for better event descriptions
    const titleMapper = (eventType: string, _eventData?: unknown) => {
      const titleMap: Record<string, string> = {
        'certifiable_system.created': 'System Created',
        'certifiable_system.updated': 'System Updated',
        'certifiable_system.archived': 'System Archived',
        'certifiable_system.restored': 'System Restored',
        'certifiable_system.state_changed': 'Approval State Changed',
        'certifiable_system.imported': 'Data Imported',
        'document.uploaded': 'Document Uploaded',
        'certifiable_system.certificate_renewed': 'Certificate Renewed',
        'certifiable_system.certificate_generated': 'Certificate Generated',
        'certifiable_system.attributes_updated': 'Attributes Updated',
      };

      return (
        titleMap[eventType] ||
        eventType
          .replace(/^[^.]+\./, '')
          .replace(/[._]/g, ' ')
          .replace(/\b\w/g, (l) => l.toUpperCase())
      );
    };

    this.dialogService.open(TimelineDialogComponent, {
      header: `Timeline - ${system.completeApprovalNumber}`,
      width: '600px',
      height: '100%',
      position: 'right',
      modal: true,
      draggable: false,
      closable: true,
      maskStyleClass: 'mask',
      contentStyle: { overflow: 'auto' },
      data: {
        entityId: system.id,
        entityType: 'CertifiableSystem',
        entityName: system.completeApprovalNumber,
        timelineService: this.approvedSystemsTimelineService,
        config: {
          entityId: system.id.toString(),
          entityType: 'CertifiableSystem',
          eventEntityTypes: ['CertifiableSystem'],
          fetchNotes: false,
          fetchEvents: true,
          itemMappers: {
            iconMapper: iconMapper,
            titleMapper: titleMapper,
            eventMapper: (event: ApplicationEvent) => {
              return {
                iconColor: iconColorMapper(event.eventType, event.eventData),
              };
            },
            contentMapper: (eventType: string, eventData?: Record<string, unknown>) => {
              // Add state info for state change events
              if (eventType === 'certifiable_system.state_changed' || eventType.includes('state_changed')) {
                if (eventData) {
                  const fromState = (eventData['source_state'] ??
                    eventData['from_state'] ??
                    eventData['previous_state']) as string;
                  const toState = (eventData['destination_state'] ??
                    eventData['to_state'] ??
                    eventData['new_state']) as string;
                  if (fromState && toState) {
                    return `State changed from ${fromState} to ${toState}`;
                  }
                }
              }
              // Add expiry date info for certificate renewal events
              if (eventType === 'certifiable_system.certificate_renewed' && eventData) {
                const newExpiry = eventData['new_expiry'] as string;
                if (newExpiry) {
                  const date = new Date(newExpiry);
                  const formattedDate = date.toLocaleDateString('en-GB');
                  return `Certificate renewed until ${formattedDate}`;
                }
              }
              // Show file name for document uploads
              if (eventType === 'document.uploaded' && eventData) {
                const fileName = (eventData['file_name'] || eventData['fileName']) as string;
                const documentType = (eventData['document_type'] || eventData['documentType']) as string;
                if (fileName) {
                  return documentType ? `${documentType}: ${fileName}` : fileName;
                }
              }
              // Show color info for color events
              if (eventType.includes('color') && eventData) {
                const colorNumber = eventData['color_number'] as string;
                const previousStatus = eventData['previous_status'] as string;
                const newStatus = eventData['new_status'] as string;
                const status = eventData['status'] as string;
                const notes = eventData['notes'] as string;

                let content = '';
                if (eventType.includes('added') && colorNumber) {
                  content = `Color ${colorNumber} added as ${status || 'Banned'}`;
                } else if (eventType.includes('updated') && colorNumber) {
                  if (previousStatus && newStatus && previousStatus !== newStatus) {
                    content = `Color ${colorNumber}: ${previousStatus} → ${newStatus}`;
                  } else {
                    content = `Color ${colorNumber} updated`;
                  }
                } else if (eventType.includes('removed') && colorNumber) {
                  content = `Color ${colorNumber} removed`;
                }

                if (notes && content) {
                  content += `\nNote: ${notes}`;
                }
                return content;
              }
              return '';
            },
          },
        },
      },
    });
  }
}
